// command-ui.ts — the SHARED in-game COMMAND UI module: the mount/unmount glue for the
// build/order/research command overlays (the gob CONTEXT MENU + the unit/structure/research
// BUILD carousels + the "Are you sure?" CONFIRM dialog + the SECTION-ONLY HELP panel).
//
// This is the EXACT mount/unmount logic that lived inline in client/src/web/main.ts
// (the Phase 5 build/order STEP 1–3 blocks), lifted into one reusable factory so BOTH the
// shipped client AND the mission editor's Play host can stand up the REAL in-game UI from a
// playtest session — instead of each host re-deriving it. main.ts adopts this later; for now
// the editor Play host is the first consumer.
//
// Each handler OWNS its own root <div> (the SAME ids main.ts used — #dom-unitmenu,
// #dom-buildpanel, #dom-structurepanel, #dom-upgradepanel, #dom-confirm, #dom-help) and its
// own view instance: mounting replaces any open instance (a fresh menu/panel replaces the
// prior one), and unmount is idempotent. teardownAll() unmounts everything (game-over).
//
// COMMAND-ADJACENT, DISPLAY-ONLY: like the views it drives, this module builds NO command and
// constructs NO Message — the choices report back through the session's onSelect/onOrder/…
// continuations (the EXACT enqueue stays in buildui.ts). Framework-free DOM, matching main.ts.

import type { TexAtlasMgr } from '../../gfx/texatlasmgr.ts';
import type { PackFileReader } from '../../io/packfile.ts';
import {
  extractAtlasArt,
  titleArtToDataUrl,
  extractButtonArt,
  resolveHelpImageDataUrl,
  type ButtonArt,
} from '../title-art.ts';
import { ConfirmView } from './confirm-view.ts';
import { HelpView } from './help-view.ts';
import { UnitMenuView } from './unit-menu-view.ts';
import { BuildPanelView } from './build-panel-view.ts';
import type { UnitMenuModel, BuildPanelModel } from '../../ui/buildui.ts';

/** Host-provided art + scale + sfx for the command overlays. */
export interface CommandUiDeps {
  /** the texture atlas mgr — extractAtlasArt/extractButtonArt source the form backdrop, the
   *  pip/damage meter glyphs, the header ?/X bitmaps, and (with {@link pakr}) the help-section
   *  inline unit/structure thumbnails. */
  tam: TexAtlasMgr;
  /** the pack reader — needed by the HELP overlay to resolve its inline <IMG SRC="*.anir">
   *  thumbnails (resolveHelpImageDataUrl over pakr+tam, the SAME path the canvas HelpControl
   *  draws). Absent ⇒ the help section renders without its inline images (bracketed alt text). */
  pakr?: PackFileReader;
  /** the CHROME magnification (= main.ts kUiMagnify): the unit-menu buttons + carousel icons +
   *  list chrome are drawn at game px × this, decoupled from the map scale so tap targets grow
   *  on phones. */
  magnify: number;
  /** the MAP present scale (= main.ts kGameScale): game px → CSS px for anchored overlays (the
   *  unit-menu's placement OVER the gob, the confirm dialog's centre), so they track the canvas
   *  which presents the playfield at this factor. Defaults to {@link magnify}. */
  gameScale?: number;
  /** the element the overlay roots mount into (default document.body). The views' roots are
   *  `position:fixed; inset:0`, so in the shipped client they span the window (== the
   *  playfield). An embedding host whose playfield is only part of the window (the mission
   *  editor's play pane) passes a TRANSFORMED pane-covering element instead — a transformed
   *  ancestor is the containing block for fixed descendants, so the same roots (and all the
   *  views' root-relative layout) bound to the pane. */
  host?: HTMLElement;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx: (sfx: number) => void;
  /** optional pre-extracted 9-slice button art (the shell/confirm pack frame). If omitted it
   *  is extracted once via extractButtonArt(tam). */
  objBtnArt?: ButtonArt | null;
}

/** The mounted command UI: the session-opt handlers + an idempotent teardown-all. */
export interface CommandUi {
  /** domUnitMenu — the gob CONTEXT MENU (BuildUI.PopupMenu). */
  domUnitMenu: (
    getModel: () => UnitMenuModel | null,
    onSelect: (idc: number) => void,
    onClose: () => void,
  ) => void;
  /** domConfirm — the "Are you sure?" CONFIRM dialog (BuildUI.PopupConfirmation). */
  domConfirm: (
    title: string,
    titleColor: string,
    anchor: { cx: number; cy: number } | null,
    onResult: (confirmed: boolean) => void,
  ) => void;
  /** domHelp — the SECTION-ONLY in-game HELP overlay (a build panel's "?"). */
  domHelp: (anchor: string) => void;
  /** domBuildPanel — the UNIT BUILD carousel (VTS "ORDER VEHICLE" / HRC "RECRUIT PERSONNEL"). */
  domBuildPanel: (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onOrder: () => void,
    onCancelOrder: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** domStructurePanel — the STRUCTURE BUILD carousel (kidfBuildStructure "ORDER BUILDING"). */
  domStructurePanel: (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onBuild: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  /** domUpgradePanel — the RESEARCH carousel (kidfUpgrade "R&D TECHNOLOGIES"). */
  domUpgradePanel: (
    getModel: () => BuildPanelModel | null,
    onSelect: (upgm: number) => void,
    onResearch: () => void,
    onClose: () => void,
    onHelp: () => void,
  ) => void;
  // per-overlay idempotent unmounts (a no-op when that overlay is down) — main.ts assigns these
  // to its module-level *Unmount let vars (the wiUi.* preview seams) and the game-over teardown.
  unmountConfirm(): void;
  unmountHelp(): void;
  unmountUnitMenu(): void;
  unmountBuildPanel(): void;
  unmountStructurePanel(): void;
  unmountUpgradePanel(): void;
  /** idempotently unmount any open menu/panel/dialog (game-over teardown). */
  teardownAll(): void;
}

/** Stand up the shared in-game command overlays for one play session. Each handler lazily owns
 *  a root <div> + a view instance; mounting replaces any open instance; unmount is idempotent. */
export function createCommandUI(deps: CommandUiDeps): CommandUi {
  const { tam, pakr, magnify, playSfx } = deps;
  const gameScale = deps.gameScale ?? magnify;
  const hostEl = deps.host ?? document.body;
  // the shared 9-slice button frame (the shell/confirm pack art) — extracted once unless the
  // host already has it (main.ts threads its module-level objBtnArt).
  const objBtnArt: ButtonArt | undefined = deps.objBtnArt ?? extractButtonArt(tam) ?? undefined;
  const playSfxFn = (sfx: number): void => {
    playSfx(sfx);
  };

  // Create a hidden root <div id=id> appended to the host (the SAME ids main.ts used). */
  function makeRoot(id: string): HTMLDivElement {
    const root = document.createElement('div');
    root.id = id;
    root.style.display = 'none';
    hostEl.appendChild(root);
    return root;
  }

  // ── confirm (#dom-confirm) — the in-game "Are you sure?" overlay (main.ts:1556) ──────────
  const confirmRoot = makeRoot('dom-confirm');
  let confirmView: ConfirmView | null = null;
  const unmountConfirm = (): void => {
    confirmRoot.style.display = 'none';
    confirmView?.destroy();
    confirmView = null;
    confirmRoot.replaceChildren();
  };
  const mountConfirm = (
    title: string,
    titleColor: string,
    anchor: { cx: number; cy: number } | null,
    onResult: (confirmed: boolean) => void,
  ): void => {
    unmountConfirm(); // destroy any prior view first (no leaked listeners/dropped done)
    confirmRoot.style.display = 'block';
    confirmView = new ConfirmView({
      root: confirmRoot,
      title,
      titleColor,
      anchor,
      magnify: gameScale,
      buttonArt: objBtnArt,
      playSfx: playSfxFn,
      // report the choice: unmount FIRST, then run the continuation (YES enqueues the command
      // back in buildui.ts; NO does nothing) so a follow-on isn't behind us.
      onResult: (confirmed: boolean) => {
        unmountConfirm();
        onResult(confirmed);
      },
    });
  };

  // ── help (#dom-help) — the SECTION-ONLY in-game HELP overlay (main.ts:1591) ──────────────
  const helpRoot = makeRoot('dom-help');
  let helpView: HelpView | null = null;
  const unmountHelp = (): void => {
    helpRoot.style.display = 'none';
    helpView?.destroy();
    helpView = null;
    helpRoot.replaceChildren();
  };
  const mountHelp = (anchor: string): void => {
    unmountHelp(); // a fresh "?" replaces any open help panel
    helpRoot.style.display = 'block';
    helpView = new HelpView({
      root: helpRoot,
      anchor,
      // resolve the section's inline <IMG SRC="*.anir"> thumbnails to the pack's team-coloured
      // 'help'-strip frame (the SAME ClientAnimationData path the canvas HelpControl uses). When
      // no pakr is available the resolver yields null → bracketed alt text (never crashes).
      resolveHelpImage: (src: string) =>
        pakr !== undefined ? resolveHelpImageDataUrl(pakr, tam, src) : null,
      playSfx: playSfxFn,
      // Close X / backdrop / Esc → just unmount (the build panel underneath stays open).
      onClose: () => {
        unmountHelp();
      },
    });
  };

  // ── unit menu (#dom-unitmenu) — the gob CONTEXT MENU overlay (main.ts:1794) ──────────────
  const unitMenuRoot = makeRoot('dom-unitmenu');
  let unitMenuView: UnitMenuView | null = null;
  const unmountUnitMenu = (): void => {
    unitMenuRoot.style.display = 'none';
    unitMenuView?.destroy();
    unitMenuView = null;
    unitMenuRoot.replaceChildren();
  };
  const mountUnitMenu = (
    getModel: () => UnitMenuModel | null,
    onSelect: (idc: number) => void,
    onClose: () => void,
  ): void => {
    unmountUnitMenu(); // a fresh menu replaces any open one (re-tap / a new gob)
    unitMenuRoot.style.display = '';
    unitMenuView = new UnitMenuView({
      root: unitMenuRoot,
      getModel,
      // a tap routes idc → OnMenuItemSelected; unmount FIRST so a follow-on overlay (Sell's
      // confirm, the build panel) isn't behind us.
      onSelect: (idc: number) => {
        unmountUnitMenu();
        onSelect(idc);
      },
      onClose: () => {
        unmountUnitMenu();
        onClose();
      },
      // chrome follows the decoupled UI scale (kUiMagnify); the PLACEMENT over the gob stays on
      // the map scale (kGameScale) so it tracks the canvas.
      magnify,
      anchorMagnify: gameScale,
      buttonArt: objBtnArt,
      playSfx: playSfxFn,
    });
  };

  // The build/order panels share the form backdrop + meter-glyph + header art (extracted once,
  // the SAME way main.ts does). buildPanelArt's pip/damage glyphs are the REAL canvas bitmaps.
  const buildPanelArt = {
    backdrop: extractAtlasArt(tam, 'buildformbkgd.png'),
    pip: extractAtlasArt(tam, 'pip.png'),
    damageInfantry: extractAtlasArt(tam, 'damage_infantry.png'),
    damageVehicle: extractAtlasArt(tam, 'damage_vehicle.png'),
    damageStructure: extractAtlasArt(tam, 'damage_structure.png'),
  };
  // the header ?/X button bitmaps (forms.ini kidcHelp/kidcCancel up/down) — absent ⇒ the view's
  // flat-box fallback.
  const helpUpArt = extractAtlasArt(tam, 'helpup.png');
  const helpDownArt = extractAtlasArt(tam, 'helpdown.png');
  const cancelUpArt = extractAtlasArt(tam, 'cancelup.png');
  const cancelDownArt = extractAtlasArt(tam, 'canceldown.png');
  const buildPanelHeaderArt =
    helpUpArt !== null && helpDownArt !== null && cancelUpArt !== null && cancelDownArt !== null
      ? {
          helpUp: titleArtToDataUrl(helpUpArt),
          helpDown: titleArtToDataUrl(helpDownArt),
          cancelUp: titleArtToDataUrl(cancelUpArt),
          cancelDown: titleArtToDataUrl(cancelDownArt),
        }
      : undefined;

  // ── build panel (#dom-buildpanel) — the UNIT BUILD carousel (main.ts:1867) ───────────────
  const buildPanelRoot = makeRoot('dom-buildpanel');
  let buildPanelView: BuildPanelView | null = null;
  const unmountBuildPanel = (): void => {
    buildPanelRoot.style.display = 'none';
    buildPanelView?.destroy();
    buildPanelView = null;
    buildPanelRoot.replaceChildren();
  };
  const mountBuildPanel = (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onOrder: () => void,
    onCancelOrder: () => void,
    onClose: () => void,
    onHelp: () => void,
  ): void => {
    unmountBuildPanel(); // a fresh panel replaces any open one (a different builder)
    buildPanelRoot.style.display = '';
    buildPanelView = new BuildPanelView({
      root: buildPanelRoot,
      getModel,
      // selection/order route back into the form (the command enqueue stays in buildui.ts).
      onSelect,
      onOrder,
      onCancelOrder,
      onClose: () => {
        unmountBuildPanel();
        onClose();
      },
      onHelp, // "?" overlays the section help (this panel stays open underneath)
      magnify,
      art: buildPanelArt,
      headerArt: buildPanelHeaderArt,
      buttonArt: objBtnArt,
      playSfx: playSfxFn,
    });
  };

  // ── structure panel (#dom-structurepanel) — the STRUCTURE BUILD carousel (main.ts:1911) ──
  const structurePanelRoot = makeRoot('dom-structurepanel');
  let structurePanelView: BuildPanelView | null = null;
  const unmountStructurePanel = (): void => {
    structurePanelRoot.style.display = 'none';
    structurePanelView?.destroy();
    structurePanelView = null;
    structurePanelRoot.replaceChildren();
  };
  const mountStructurePanel = (
    getModel: () => BuildPanelModel | null,
    onSelect: (ut: number) => void,
    onBuild: () => void,
    onClose: () => void,
    onHelp: () => void,
  ): void => {
    unmountStructurePanel();
    structurePanelRoot.style.display = '';
    structurePanelView = new BuildPanelView({
      root: structurePanelRoot,
      getModel,
      onSelect,
      // "Build" returns a gt → BuildUI runs the gpfrmPlace handoff (NO command from the view).
      onBuild: () => {
        unmountStructurePanel();
        onBuild();
      },
      onClose: () => {
        unmountStructurePanel();
        onClose();
      },
      onHelp,
      magnify,
      art: buildPanelArt,
      headerArt: buildPanelHeaderArt,
      buttonArt: objBtnArt,
      playSfx: playSfxFn,
    });
  };

  // ── upgrade panel (#dom-upgradepanel) — the RESEARCH carousel (main.ts:1954) ─────────────
  const upgradePanelRoot = makeRoot('dom-upgradepanel');
  let upgradePanelView: BuildPanelView | null = null;
  const unmountUpgradePanel = (): void => {
    upgradePanelRoot.style.display = 'none';
    upgradePanelView?.destroy();
    upgradePanelView = null;
    upgradePanelRoot.replaceChildren();
  };
  const mountUpgradePanel = (
    getModel: () => BuildPanelModel | null,
    onSelect: (upgm: number) => void,
    onResearch: () => void,
    onClose: () => void,
    onHelp: () => void,
  ): void => {
    unmountUpgradePanel();
    upgradePanelRoot.style.display = '';
    upgradePanelView = new BuildPanelView({
      root: upgradePanelRoot,
      getModel,
      onSelect,
      // "Research" returns the upgm → BuildUI enqueues kmidUpgradeCommand (NO command here).
      onResearch: () => {
        unmountUpgradePanel();
        onResearch();
      },
      onClose: () => {
        unmountUpgradePanel();
        onClose();
      },
      onHelp,
      magnify,
      art: buildPanelArt,
      headerArt: buildPanelHeaderArt,
      buttonArt: objBtnArt,
      playSfx: playSfxFn,
    });
  };

  // game-over / host close: unmount every overlay (idempotent — each unmount is a no-op when
  // nothing is open). Leaves the empty root divs in the DOM (cheap; reused on the next mount).
  const teardownAll = (): void => {
    unmountConfirm();
    unmountHelp();
    unmountUnitMenu();
    unmountBuildPanel();
    unmountStructurePanel();
    unmountUpgradePanel();
  };

  return {
    domUnitMenu: mountUnitMenu,
    domConfirm: mountConfirm,
    domHelp: mountHelp,
    domBuildPanel: mountBuildPanel,
    domStructurePanel: mountStructurePanel,
    domUpgradePanel: mountUpgradePanel,
    unmountConfirm,
    unmountHelp,
    unmountUnitMenu,
    unmountBuildPanel,
    unmountStructurePanel,
    unmountUpgradePanel,
    teardownAll,
  };
}
